import { api } from './client';
import {
  AdminFeedModelDetails,
  AdminFeedModelPhotoResponse,
  AdminFeedModelSummary,
  AdminFeedModelVideoResponse,
  AdminFeedPinnedModel,
  AdminFeedPinnedModelRequest,
  AdminFeedVideoRequest,
  FeedSyncResponse,
  FeedVerificationResponse,
  ClickStatsResponse,
  ClickStatsV2Response,
  ClicksByPageResponse,
  ClicksPagesOverviewResponse,
  DailyClickStats,
  ModelClickStats
} from './types';

export const listFeedModels = async (): Promise<AdminFeedModelSummary[]> => {
  const { data } = await api.get<AdminFeedModelSummary[]>('/admin/feed/models');
  return data;
};

export const getFeedModel = async (modelUsername: string): Promise<AdminFeedModelDetails> => {
  const { data } = await api.get<AdminFeedModelDetails>(`/admin/feed/models/${encodeURIComponent(modelUsername)}`);
  return data;
};

export const addFeedPhoto = async (modelUsername: string, fileName: string): Promise<AdminFeedModelPhotoResponse> => {
  const { data } = await api.post<AdminFeedModelPhotoResponse>(`/admin/feed/models/${encodeURIComponent(modelUsername)}/photos`, {
    fileName
  });
  return data;
};

export const deactivateFeedPhoto = async (
  modelUsername: string,
  fileName: string
): Promise<AdminFeedModelPhotoResponse> => {
  const { data } = await api.post<AdminFeedModelPhotoResponse>(
    `/admin/feed/models/${encodeURIComponent(modelUsername)}/photos/deactivate`,
    { fileName }
  );
  return data;
};

export const deactivateFeedModel = async (
  modelUsername: string,
  fileName?: string | null
): Promise<AdminFeedModelDetails> => {
  const { data } = await api.post<AdminFeedModelDetails>(
    `/admin/feed/models/${encodeURIComponent(modelUsername)}/deactivate`,
    fileName ? { fileName } : undefined
  );
  return data;
};

export const syncFeedPromotedModels = async (): Promise<FeedSyncResponse> => {
  const { data } = await api.post<FeedSyncResponse>('/admin/feed/promoted/sync');
  return data;
};

export const verifyFeedPromotedModels = async (): Promise<FeedVerificationResponse> => {
  const { data } = await api.post<FeedVerificationResponse>('/admin/feed/promoted/verify');
  return data;
};

export const listPinnedFeedModels = async (): Promise<AdminFeedPinnedModel[]> => {
  const { data } = await api.get<AdminFeedPinnedModel[]>('/admin/feed/pinned');
  return data;
};

export const upsertPinnedFeedModel = async (
  modelUsername: string,
  payload: AdminFeedPinnedModelRequest
): Promise<AdminFeedPinnedModel> => {
  const { data } = await api.put<AdminFeedPinnedModel>(
    `/admin/feed/pinned/${encodeURIComponent(modelUsername)}`,
    payload
  );
  return data;
};

export const deletePinnedFeedModel = async (modelUsername: string): Promise<void> => {
  await api.delete(`/admin/feed/pinned/${encodeURIComponent(modelUsername)}`);
};

// Video API functions

export const listFeedVideos = async (modelUsername: string): Promise<AdminFeedModelVideoResponse[]> => {
  const { data } = await api.get<AdminFeedModelVideoResponse[]>(
    `/admin/feed/models/${encodeURIComponent(modelUsername)}/videos`
  );
  return data;
};

export const addFeedVideo = async (
  modelUsername: string,
  request: AdminFeedVideoRequest
): Promise<AdminFeedModelVideoResponse> => {
  const { data } = await api.post<AdminFeedModelVideoResponse>(
    `/admin/feed/models/${encodeURIComponent(modelUsername)}/videos`,
    request
  );
  return data;
};

export const deactivateFeedVideo = async (
  modelUsername: string,
  fileName: string
): Promise<AdminFeedModelVideoResponse> => {
  const { data } = await api.post<AdminFeedModelVideoResponse>(
    `/admin/feed/models/${encodeURIComponent(modelUsername)}/videos/deactivate`,
    { fileName }
  );
  return data;
};

// Click Statistics API functions

export const getClickStats = async (): Promise<ClickStatsResponse> => {
  const { data } = await api.get<ClickStatsResponse>('/admin/feed/clicks/stats');
  return data;
};

export const getDailyClickStats = async (days: number = 30): Promise<DailyClickStats[]> => {
  const { data } = await api.get<DailyClickStats[]>('/admin/feed/clicks/daily', {
    params: { days }
  });
  return data;
};

export const getTopModelsByClicks = async (
  limit: number = 20,
  days: number = 30
): Promise<ModelClickStats[]> => {
  const { data } = await api.get<ModelClickStats[]>('/admin/feed/clicks/top-models', {
    params: { limit, days }
  });
  return data;
};

/**
 * Все 3 v2 API принимают опциональные фильтры:
 *  - domain        : 'osr' | 'o2s' | 'sf' | 'fs' | 'main'
 *  - monetization  : 'PPC' | 'CPL' | 'OF' | 'REVSHARE'
 *  - tiers         : 'tier1,tier2' (geo-фильтр — backend маппит в countries CSV)
 * По умолчанию (отсутствие параметра) = "all".
 */
export interface ClicksFilters {
  domain?: string;
  monetization?: string;
  tiers?: string;
}

function appendFilters(params: Record<string, string | number>, f?: ClicksFilters) {
  if (!f) return;
  if (f.domain)       params.domain = f.domain;
  if (f.monetization) params.monetization = f.monetization;
  if (f.tiers)        params.tiers = f.tiers;
}

export const getClickStatsV2 = async (
  days: number = 30,
  filters?: ClicksFilters
): Promise<ClickStatsV2Response> => {
  const params: Record<string, string | number> = { days };
  appendFilters(params, filters);
  const { data } = await api.get<ClickStatsV2Response>('/admin/feed/clicks/v2', { params });
  return data;
};

export const getClicksByPage = async (
  pageKey: string,
  days: number = 30,
  filters?: ClicksFilters
): Promise<ClicksByPageResponse> => {
  const params: Record<string, string | number> = { pageKey, days };
  appendFilters(params, filters);
  const { data } = await api.get<ClicksByPageResponse>('/admin/feed/clicks/v2/by-page', { params });
  return data;
};

export const getClicksPagesOverview = async (
  days: number = 30,
  filters?: ClicksFilters
): Promise<ClicksPagesOverviewResponse> => {
  const params: Record<string, string | number> = { days };
  appendFilters(params, filters);
  const { data } = await api.get<ClicksPagesOverviewResponse>('/admin/feed/clicks/v2/pages-overview', { params });
  return data;
};
